import React, { useMemo } from 'react';
import styled, { css } from 'styled-components';

import { Target } from '../../../../client';
import BoxGradientBorder from '../../../../components/BoxGradientBorder';
import { withOpacity } from '../../../../styled-components/dark/helpers';
import palette from '../../../../styled-components/palette';
import { DefaultMissionBackground } from '../../assets/img';
import { MissionCardComplexeStatuses, type MissionCardComplexeStatus } from '../../types/components';
import MissionBoxWrapper from '../common/MissionBoxWrapper';
import MissionFreebetsPotentialGainContainer from '../MissionFreebetsPotentialGain/MissionFreebetsPotentialGainContainer';
import MissionTimerContainer from '../MissionTimer/MissionTimerContainer';

const CARD_BORDER_RADIUS = 1; // em

const emojiList = ['💪', '💸', '🤑'];

const Wrapper = styled(BoxGradientBorder)`
  position: relative;
  color: white;
  padding: 0px;

  border-radius: ${CARD_BORDER_RADIUS}em;
  --BoxGradientBorder-width: 1px;
  --BoxGradientBorder-gradient: linear-gradient(to bottom, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.1) -4%);
  --BoxGradientBorder-fallbackColor: rgba(255, 255, 255, 0.1);
  --BoxGradientBorder-afterZIndex: 5;
`;

const ContentWrapper = styled.div`
  transform-style: preserve-3d;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  min-height: 8em;
`;

type ImageBackgroundType = {
  $imageURL?: string | null;
};

const ImageBackground = styled.div<ImageBackgroundType>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 1em;

  transform: translateZ(0px);

  ${({ $imageURL }) => css`
    background-image: url(${$imageURL ?? DefaultMissionBackground});
    background-position: start center;
    background-repeat: no-repeat;
    background-size: 100%, auto;
  `};
`;

type OverlayType = {
  $missionStatus: MissionCardComplexeStatus;
};

const Overlay = styled.div<OverlayType>`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  border-radius: ${CARD_BORDER_RADIUS}em;
  background: #00000077;

  font-size: min(90%, 4vmin);
  transform: translateZ(100px);

  pointer-events: none;

  ${({ $missionStatus }) => {
    switch ($missionStatus) {
      case MissionCardComplexeStatuses.EXPIRED_BUT_PENDING_BETS:
      case MissionCardComplexeStatuses.EXPIRED:
        return css`
          opacity: 1;
        `;
      default:
        return css`
          transition: opacity 300ms ease-in-out;
          opacity: 0;
          pointer-events: none;
        `;
    }
  }}
`;
const WonCardOverlay = styled.div`
  position: absolute;
  height: 100%;
  width: 100%;
  top: 0;
  left: 0;

  border-radius: ${CARD_BORDER_RADIUS}em;
  background: #00000077;
  transform: translateZ(2px);
  pointer-events: none;

  opacity: 1;
  background: linear-gradient(
    to bottom,
    ${withOpacity(palette.greenwinamax650, 'ee')},
    ${withOpacity(palette.greenwinamax650, 'aa')} 40%,
    rgba(255, 255, 255, 0) 100%
  );
`;

const EmojiWrapper = styled.div`
  font-size: 120%;
`;

const Logo = styled.img`
  object-fit: contain;
  width: 100%;
  max-height: ${Target.__MOBILE__ ? 5.2 : 6}em;
  align-self: center;
  transform: translateZ(1px);
  margin: 2.7em 0;
`;

const EmptyLogo = styled.div`
  object-fit: contain;
  width: ${Target.__MOBILE__ ? 5.2 : 6}em;
  height: ${Target.__MOBILE__ ? 5.2 : 6}em;
  margin: 2.7em 0;
`;

type TopRowType = {
  $displayShadow: boolean;
};

const TopRow = styled.div<TopRowType>`
  position: absolute;
  width: 100%;
  top: 0;
  left: 0;

  transform: translateZ(3px);
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.67em 1em 0.67em;
  border-radius: ${CARD_BORDER_RADIUS}em ${CARD_BORDER_RADIUS}em 0 0;

  transform-style: preserve-3d;

  ${({ $displayShadow }) =>
    $displayShadow
      ? css`
          background-image: linear-gradient(to bottom, rgba(0, 0, 0, 0.6) 55%, rgba(0, 0, 0, 0));
        `
      : undefined}
`;

const TopRowItem = styled.div`
  flex: 1;

  display: flex;
  align-items: center;
  justify-content: flex-end;

  gap: 0.66em;
`;

const StyledMissionTimerContainer = styled(MissionTimerContainer)`
  transform: translateZ(0);
`;

type MissionFreebetsPotentialGainWrapperType = {
  $isWon: boolean;
};

const MissionFreebetsPotentialGainWrapper = styled(MissionBoxWrapper)<MissionFreebetsPotentialGainWrapperType>`
  overflow: visible;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0.1em 0.6em;
  border-radius: 0.5em;

  ${({ $isWon }) =>
    $isWon
      ? css`
          background-image: linear-gradient(
            to top,
            ${withOpacity(palette.greenwinamax500, '54')},
            ${withOpacity(palette.greenwinamax500, '22')}
          );
        `
      : undefined}
`;

const StyledMissionFreebetsPotentialGainContainer = styled(MissionFreebetsPotentialGainContainer)`
  font-size: 120%;
`;

type MissionDetailsSummaryProps = {
  className?: string;
  missionId: number;
  logoURL?: string | null;
  backgroundURL?: string | null;
  missionStatus: MissionCardComplexeStatus;
  showTimer: boolean;
  hasPotentialAmount: boolean;
};

const MissionDetailsSummary = ({
  className = '',
  missionId,
  logoURL,
  backgroundURL,
  missionStatus,
  showTimer,
  hasPotentialAmount,
}: MissionDetailsSummaryProps) => {
  const missionIsExpired = useMemo(
    () =>
      missionStatus === MissionCardComplexeStatuses.EXPIRED ||
      missionStatus === MissionCardComplexeStatuses.EXPIRED_BUT_PENDING_BETS,
    [missionStatus],
  );

  const displayTimer = useMemo(() => missionIsExpired || showTimer, [missionIsExpired, showTimer]);

  const isWon = useMemo(() => missionStatus === MissionCardComplexeStatuses.WON, [missionStatus]);

  return (
    <Wrapper className={className}>
      <ContentWrapper>
        <ImageBackground $imageURL={backgroundURL} />
        <Overlay $missionStatus={missionStatus} />
        {isWon ? <WonCardOverlay /> : null}
        {logoURL ? <Logo src={logoURL} /> : <EmptyLogo />}
        <TopRow $displayShadow={displayTimer || hasPotentialAmount}>
          {displayTimer ? <StyledMissionTimerContainer missionId={missionId} /> : null}
          {hasPotentialAmount ? (
            <TopRowItem>
              {isWon ? <EmojiWrapper>{emojiList[missionId % 3]}</EmojiWrapper> : null}
              <MissionFreebetsPotentialGainWrapper $isWon={isWon}>
                <StyledMissionFreebetsPotentialGainContainer missionId={missionId} smallIcon />
              </MissionFreebetsPotentialGainWrapper>
            </TopRowItem>
          ) : null}
        </TopRow>
      </ContentWrapper>
    </Wrapper>
  );
};
export default MissionDetailsSummary;
